import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Mail, Edit, Eye, Send, Save, RefreshCw, Bell, CreditCard, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  
  const [editForm, setEditForm] = useState({
    name: '',
    subject: '',
    body_html: ''
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await adminAPI.getEmailTemplates();
      setTemplates(res.data);
    } catch (error) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditForm({
      name: template.name,
      subject: template.subject,
      body_html: template.body_html
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    setSaving(true);
    
    try {
      await adminAPI.updateEmailTemplate(selectedTemplate.template_key, editForm);
      toast.success('Template updated successfully');
      setEditDialogOpen(false);
      loadTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (template) => {
    try {
      const res = await adminAPI.previewEmailTemplate(template.template_key);
      setPreview(res.data);
      setSelectedTemplate(template);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const handleSendTest = async () => {
    if (!selectedTemplate || !testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    
    try {
      await adminAPI.sendTestEmail(selectedTemplate.template_key, testEmail);
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error('Failed to send test email');
    }
  };

  const handleSendWeeklyReminders = async () => {
    if (!window.confirm('Send weekly payment reminders to all members with pending online payments?')) return;
    
    setSendingReminders(true);
    try {
      const res = await adminAPI.sendWeeklyPaymentReminders();
      toast.success(`Sent ${res.data.sent_count} reminder(s) successfully`);
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const getTemplateIcon = (key) => {
    const icons = {
      'user_invite': <Mail className="w-4 h-4 text-blue-500" />,
      'password_reset': <RefreshCw className="w-4 h-4 text-purple-500" />,
      'guide_request_new': <Bell className="w-4 h-4 text-amber-500" />,
      'request_accepted': <Mail className="w-4 h-4 text-green-500" />,
      'payment_confirmation': <CreditCard className="w-4 h-4 text-emerald-500" />,
      'weekly_payment_reminder': <CreditCard className="w-4 h-4 text-orange-500" />,
      'daily_summary': <FileText className="w-4 h-4 text-indigo-500" />,
      'monthly_invoice': <FileText className="w-4 h-4 text-rose-500" />
    };
    return icons[key] || <Mail className="w-4 h-4 text-slate-500" />;
  };

  return (
    <AdminLayout title="Email Templates" subtitle="Manage and customize email templates">
      <Tabs defaultValue="templates" className="space-y-4">
        <TabsList>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <Mail className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="send" data-testid="tab-send">
            <Send className="w-4 h-4 mr-2" />
            Send Emails
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>Email Templates</CardTitle>
              <CardDescription>
                Customize the content and styling of all automated emails. Use variables like {"{{name}}"} in your templates.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><div className="spinner" /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Template</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Variables</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {templates.map((template) => (
                      <TableRow key={template.template_key}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getTemplateIcon(template.template_key)}
                            <div>
                              <p className="font-medium">{template.name}</p>
                              <p className="text-xs text-slate-500 font-mono">{template.template_key}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <p className="text-sm text-slate-600 truncate">{template.description}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {template.variables?.slice(0, 3).map((v) => (
                              <Badge key={v} variant="outline" className="text-xs font-mono">
                                {v}
                              </Badge>
                            ))}
                            {template.variables?.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{template.variables.length - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreview(template)}
                              data-testid={`preview-${template.template_key}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(template)}
                              data-testid={`edit-${template.template_key}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {templates.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                          No email templates found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="send">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Weekly Payment Reminders */}
            <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-amber-900">Weekly Payment Reminders</CardTitle>
                    <CardDescription className="text-amber-700">
                      Send payment reminders to members with pending online tours
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-3 bg-white/60 rounded-lg">
                    <p className="text-sm text-amber-800">
                      This will send an email to all members who have unpaid online tour requests, 
                      reminding them to complete their weekly payment before Monday.
                    </p>
                  </div>
                  <Button
                    onClick={handleSendWeeklyReminders}
                    disabled={sendingReminders}
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    data-testid="send-weekly-reminders-btn"
                  >
                    {sendingReminders ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending Reminders...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Weekly Payment Reminders
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Daily Summary */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle>Daily Download Summary</CardTitle>
                    <CardDescription>
                      Send daily ticket download summaries to users
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">
                      Sends a summary of today's ticket downloads to all users who had activity.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={async () => {
                      try {
                        const res = await adminAPI.sendDailySummaries();
                        toast.success(`Sent ${res.data.sent_count} summary email(s)`);
                      } catch (error) {
                        toast.error('Failed to send summaries');
                      }
                    }}
                    data-testid="send-daily-summaries-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Daily Summaries
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Monthly Invoices */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-rose-600" />
                  </div>
                  <div>
                    <CardTitle>Monthly Invoices</CardTitle>
                    <CardDescription>
                      Send monthly invoices to all users with balances
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">
                      Generates and sends monthly invoices to all users with outstanding balances.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={async () => {
                      try {
                        const res = await adminAPI.sendMonthlyInvoices();
                        toast.success(`Sent ${res.data.sent_count} invoice(s)`);
                      } catch (error) {
                        toast.error('Failed to send invoices');
                      }
                    }}
                    data-testid="send-monthly-invoices-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Monthly Invoices
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Template: {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-template-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input
                value={editForm.subject}
                onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                placeholder="Use {{variable}} for dynamic content"
                data-testid="edit-template-subject"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Available Variables</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg">
                {selectedTemplate?.variables?.map((v) => (
                  <Badge key={v} variant="outline" className="font-mono text-xs cursor-pointer hover:bg-slate-200"
                    onClick={() => {
                      navigator.clipboard.writeText(`{{${v}}}`);
                      toast.success(`Copied {{${v}}} to clipboard`);
                    }}
                  >
                    {`{{${v}}}`}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Email Body (HTML)</Label>
              <Textarea
                value={editForm.body_html}
                onChange={(e) => setEditForm({ ...editForm, body_html: e.target.value })}
                rows={15}
                className="font-mono text-sm"
                placeholder="HTML content with {{variables}}"
                data-testid="edit-template-body"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-template-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Preview: {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          
          {preview && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-100 rounded-lg">
                <Label className="text-xs text-slate-500">Subject</Label>
                <p className="font-medium">{preview.subject}</p>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-800 text-white px-4 py-2 text-sm">
                  Email Preview
                </div>
                <div 
                  className="p-4 bg-white"
                  dangerouslySetInnerHTML={{ __html: preview.body_html }}
                />
              </div>
              
              <div className="border-t pt-4">
                <Label className="mb-2 block">Send Test Email</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="test@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    data-testid="test-email-input"
                  />
                  <Button onClick={handleSendTest} data-testid="send-test-btn">
                    <Send className="w-4 h-4 mr-2" />
                    Send Test
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminEmailTemplates;
